import {ICompetitionSchool} from './competition.model';

/**
 * Base model for Competition School Activity
 */
export class CompetitionSchoolActivityModel implements ICompetitionSchool {
  public _id: string;
  public competitionId: string;
  public country: string;
  public schoolId: string;
  public schoolName: string;
  public numStudents = 0;
  public numAnswers = 0;
  public numCorrect = 0;
  public avgAnswers = 0;
  public avgCorrect = 0;
  public timeSpent = 0;
  public startAt: Date;
  public endAt: Date;
  public createdAt: Date;
  public updatedAt: Date;
  public type: string; // 'competitionSchoolActivityDaily' | 'competitionSchoolActivityWeekly'

  static generateModel(json: any): CompetitionSchoolActivityModel {
    const activity = new CompetitionSchoolActivityModel();
    activity._id = json._id;
    activity.competitionId = json.competitionId;
    activity.country = json.country;
    activity.schoolId = json.schoolId;
    activity.schoolName = json.schoolName;
    activity.numStudents = json.numStudents || 0;
    activity.numAnswers = json.numAnswers || 0;
    activity.numCorrect = json.numCorrect || 0;
    activity.avgAnswers = json.avgAnswers || 0;
    activity.avgCorrect = json.avgCorrect || 0;
    activity.timeSpent = json.timeSpent || 0;
    activity.type = json.type;

    if (json.startAt) {
      activity.startAt = new Date(json.startAt);
    }

    if (json.endAt) {
      activity.endAt = new Date(json.endAt);
    }

    if (json.createdAt) {
      activity.createdAt = new Date(json.createdAt);
    }

    if (json.updatedAt) {
      activity.updatedAt = new Date(json.updatedAt);
    }

    return activity;
  }

  static generateModels(jsonList: any[]): CompetitionSchoolActivityModel[] {
    const list = [];
    for (const json of jsonList) {
      list.push(this.generateModel(json));
    }
    return list;
  }

  isDaily(): boolean {
    return this.type === 'competitionSchoolActivityDaily';
  }

  isWeekly(): boolean {
    return this.type === 'competitionSchoolActivityWeekly';
  }

  // Implementation of ICompetitionSchool interface
  getName(): string {
    return this.schoolName;
  }
}

/**
 * Model for grouped school activities (when group=1 in query)
 */
export class CompetitionSchoolActivityGroupedModel {
  public _id: Date;
  public week: Date; // For weekly activities
  public day: Date; // For daily activities
  public schools: CompetitionSchoolActivityModel[];

  static generateModel(json: any): CompetitionSchoolActivityGroupedModel {
    const grouped = new CompetitionSchoolActivityGroupedModel();
    grouped._id = json._id ? new Date(json._id) : null;
    grouped.week = json.week ? new Date(json.week) : null;
    grouped.day = json.day ? new Date(json.day) : null;

    if (json.schools && Array.isArray(json.schools)) {
      grouped.schools = CompetitionSchoolActivityModel.generateModels(json.schools);
    } else {
      grouped.schools = [];
    }

    return grouped;
  }

  static generateModels(jsonList: any[]): CompetitionSchoolActivityGroupedModel[] {
    const list = [];
    for (const json of jsonList) {
      list.push(this.generateModel(json));
    }
    return list;
  }

  /**
   * Get the date field (day or week) depending on the activity type
   */
  getDate(): Date {
    return this.day || this.week;
  }

  /**
   * Check if this is a daily grouped activity
   */
  isDaily(): boolean {
    return this.day !== null && this.day !== undefined;
  }

  /**
   * Check if this is a weekly grouped activity
   */
  isWeekly(): boolean {
    return this.week !== null && this.week !== undefined;
  }
}

/**
 * Base model for Competition Group Activity
 */
export class CompetitionGroupActivityModel {
  public _id: string;
  public competitionId: string;
  public groupId: string;
  public groupName: string;
  public country: string;
  public schoolId: string;
  public schoolName: string;
  public numAnswers = 0;
  public timeSpent = 0;
  public numStudents = 0;
  public startAt: Date;
  public endAt: Date;
  public createdAt: Date;
  public updatedAt: Date;
  public type: string; // 'competitionGroupActivityDaily' | 'competitionGroupActivityWeekly' | 'competitionGroupActivityTotal'

  static generateModel(json: any): CompetitionGroupActivityModel {
    const activity = new CompetitionGroupActivityModel();
    activity._id = json._id;
    activity.competitionId = json.competitionId;
    activity.groupId = json.groupId;
    activity.groupName = json.groupName;
    activity.country = json.country;
    activity.schoolId = json.schoolId;
    activity.schoolName = json.schoolName;
    activity.numAnswers = json.numAnswers || 0;
    activity.timeSpent = json.timeSpent || 0;
    activity.numStudents = json.numStudents || 0;
    activity.type = json.type;

    if (json.startAt) {
      activity.startAt = new Date(json.startAt);
    }

    if (json.endAt) {
      activity.endAt = new Date(json.endAt);
    }

    if (json.createdAt) {
      activity.createdAt = new Date(json.createdAt);
    }

    if (json.updatedAt) {
      activity.updatedAt = new Date(json.updatedAt);
    }

    return activity;
  }

  static generateModels(jsonList: any[]): CompetitionGroupActivityModel[] {
    const list = [];
    for (const json of jsonList) {
      list.push(this.generateModel(json));
    }
    return list;
  }

  isDaily(): boolean {
    return this.type === 'competitionGroupActivityDaily';
  }

  isWeekly(): boolean {
    return this.type === 'competitionGroupActivityWeekly';
  }

  isTotal(): boolean {
    return this.type === 'competitionGroupActivityTotal';
  }
}

/**
 * Base model for Competition Student Activity
 */
export class CompetitionStudentActivityModel {
  public _id: string;
  public competitionId: string;
  public country: string;
  public studentId: string;
  public studentName: string;
  public schoolIds: string[] = [];
  public schoolNames: string[] = [];
  public numAnswers = 0;
  public timeSpent = 0;
  public startAt: Date;
  public endAt: Date;
  public createdAt: Date;
  public updatedAt: Date;
  public type: string; // 'competitionStudentActivityDaily' | 'competitionStudentActivityWeekly' | 'competitionStudentActivityTotal'

  static generateModel(json: any): CompetitionStudentActivityModel {
    const activity = new CompetitionStudentActivityModel();
    activity._id = json._id;
    activity.competitionId = json.competitionId;
    activity.country = json.country;
    activity.studentId = json.studentId;
    activity.studentName = json.studentName;
    activity.schoolIds = json.schoolIds || [];
    activity.schoolNames = json.schoolNames || [];
    activity.numAnswers = json.numAnswers || 0;
    activity.timeSpent = json.timeSpent || 0;
    activity.type = json.type;

    if (json.startAt) {
      activity.startAt = new Date(json.startAt);
    }

    if (json.endAt) {
      activity.endAt = new Date(json.endAt);
    }

    if (json.createdAt) {
      activity.createdAt = new Date(json.createdAt);
    }

    if (json.updatedAt) {
      activity.updatedAt = new Date(json.updatedAt);
    }

    return activity;
  }

  static generateModels(jsonList: any[]): CompetitionStudentActivityModel[] {
    const list = [];
    for (const json of jsonList) {
      list.push(this.generateModel(json));
    }
    return list;
  }

  isDaily(): boolean {
    return this.type === 'competitionStudentActivityDaily';
  }

  isWeekly(): boolean {
    return this.type === 'competitionStudentActivityWeekly';
  }

  isTotal(): boolean {
    return this.type === 'competitionStudentActivityTotal';
  }
}

/**
 * Filter interface for Competition School Activities
 */
export class CompetitionSchoolActivityFilter {
  skip = 0;
  limit = 20;
  type: 'daily' | 'weekly' = 'weekly';
  country: string = undefined;
  name: string = undefined;
  schoolIds: string = undefined; // Comma-separated string
  startAt: Date = undefined;
  endAt: Date = undefined;
  sortByStartAt: number = undefined; // 1 for ascending, -1 for descending (sorts individual items)
  sortByNumAnswers: number = undefined;
  sortByNumCorrect: number = undefined;
  sortByAvgAnswers: number = undefined; // 1 for ascending, -1 for descending
  sortByAvgCorrect: number = undefined;
  sortByTimespent: number = undefined;
  group: boolean = undefined; // Group by week/day (use 1 for true in query string)
  sortGroupByStartAt: number = undefined; // 1 for ascending, -1 for descending (sorts groups when group=1)
}

/**
 * Filter interface for Competition Group Activities
 */
export class CompetitionGroupActivityFilter {
  skip = 0;
  limit = 20;
  type: 'daily' | 'weekly' | 'total' = 'weekly';
  country: string = undefined;
  name: string = undefined;
  schoolIds: string = undefined; // Comma-separated string
  groupIds: string = undefined; // Comma-separated string
  startAt: Date = undefined;
  endAt: Date = undefined;
  sortByStartAt: number = undefined;
  sortByNumAnswers: number = undefined;
  sortByTimespent: number = undefined;
  group: boolean = undefined; // Group by week/day
}

